/**
 * CSV amount parsing — the SINGLE isomorphic source of truth for CLEANING a CSV
 * amount cell (currency symbols, EU/US separators) and turning it into a
 * number. Used by BOTH the backend import pipeline and the frontend
 * rule/preview helpers, so the two never diverge.
 *
 * NO formatea importes de vuelta a cadena: el formateador que vivía aquí
 * (`formatCsvAmount`, `toFixed(8)` sobre un float) se retiró con el issue
 * #2197. Ver la nota en {@link parseCsvAmount}.
 *
 * Divergent amount parsing is tax-critical: a value that parses in one path but
 * not another corrupts cost basis. Previously the backend
 * (`imports/utils/csv-amount-parser.ts`) and the frontend
 * (`import/utils/RuleParser.ts`) each carried their own byte-identical copy;
 * both now re-export these.
 *
 * SEMANTICS
 *   - Currency symbols and whitespace are stripped first. Any Unicode currency
 *     symbol (\p{Sc}: $ € £ ¥ ₿ ₮ ₩ ₹ …) qualifies, so a leading exchange glyph
 *     like QuantFury's `₮` (tether) doesn't defeat parsing.
 *   - `EU` format: `.` is the thousands separator and `,` the decimal point
 *     (`1.234,56` → 1234.56). Anything else is treated as `US`: `,` thousands,
 *     `.` decimal (`1,234.56` → 1234.56).
 *   - A cell that CONTRADICTS the declared format is REFUSED, not reinterpreted
 *     (see {@link detectAmountFormatContradiction}).
 *   - Returns `null` on empty/unparseable/contradicting input (never throws,
 *     never NaN).
 */

/**
 * Currency symbols + whitespace stripped before numeric parsing. `\p{Sc}` is
 * the Unicode "Symbol, currency" class — it covers every currency glyph
 * ($ € £ ¥ ₿ ₮ ₩ ₹ ₴ …) so we never have to enumerate them, and it never
 * matches a digit, sign, or decimal/thousands separator.
 */
export const CURRENCY_SYMBOL_PATTERN = /[\p{Sc}\s]/gu

/** The declared format is US-style but the cell is unambiguously EU-formatted. */
export const AMOUNT_LOOKS_EU = 'looks-eu'
/** The declared format is EU-style but the cell is unambiguously US-formatted. */
export const AMOUNT_LOOKS_US = 'looks-us'

export type AmountFormatMismatch = typeof AMOUNT_LOOKS_EU | typeof AMOUNT_LOOKS_US

// A number written in the US convention: plain digits, OR comma thousands-groups
// of exactly 3, plus an optional single `.` decimal tail.
//   1000 · 1,234 · 1,234,567 · 1,234.56 · 1234.56 · 0.5 · -12.00
// ⚠️ El primer grupo nunca empieza por 0: «0,399» no es 399 en US ni «0.399» lo
// es en EU; en la convención contraria son un decimal, y la celda se RECHAZA
// como contradictoria en vez de multiplicarse por mil.
const VALID_US_NUMBER = /^[+-]?(\d+|[1-9]\d{0,2}(,\d{3})+)(\.\d+)?$/

// A number written in the EU convention: plain digits, OR period thousands-groups
// of exactly 3, plus an optional single `,` decimal tail.
//   1000 · 1.234 · 1.234.567 · 1.234,56 · 1234,56 · 0,5 · -12,00
const VALID_EU_NUMBER = /^[+-]?(\d+|[1-9]\d{0,2}(\.\d{3})+)(,\d+)?$/

// Only a BARE numeric cell (optional sign, then digits and `.`/`,`) is judged.
// A cell carrying letters (a combined amount+ticker like "334.850W"), scientific
// notation ("2.8e-7"), or the lone `-` no-value sentinel is skipped so the
// detector can never false-positive on a non-plain-number cell.
const BARE_NUMERIC = /^[+-]?[\d.,]+$/

/**
 * Return which OPPOSITE convention an ALREADY-CLEANED cell appears to use when
 * it CONTRADICTS the declared `amountFormat`, or `null` when the cell is
 * consistent with it (or is not a bare number, or is ambiguous).
 *
 * CONTRADICTION, never AMBIGUITY. A cell is flagged ONLY when it is a valid
 * number in the OPPOSITE convention but NOT in the declared one. It never flags
 * a plain integer (`1000` — valid in both) nor an ambiguous single-separator
 * value (`1.000` / `1,234` — valid in both), so a correctly-formatted file is
 * never touched. It flags only genuinely contradictory shapes: a comma-decimal
 * or period-grouping under US (`0,5`, `1.234,56`), or a period-decimal or
 * comma-grouping under EU (`1.5`, `1,234.56`).
 *
 * @param cleanedValue Cell with currency symbols/whitespace already stripped.
 */
export function detectAmountFormatContradiction(
  cleanedValue: string,
  amountFormat: string,
): AmountFormatMismatch | null {
  if (cleanedValue === '' || !BARE_NUMERIC.test(cleanedValue)) {
    return null
  }

  if (amountFormat === 'EU') {
    // Consistent with EU (or ambiguous → valid in both) → not a contradiction.
    if (VALID_EU_NUMBER.test(cleanedValue)) {
      return null
    }
    // Contradicts EU but is a clean US number → the cell is US-formatted.
    return VALID_US_NUMBER.test(cleanedValue) ? AMOUNT_LOOKS_US : null
  }

  // Default / US.
  if (VALID_US_NUMBER.test(cleanedValue)) {
    return null
  }
  return VALID_EU_NUMBER.test(cleanedValue) ? AMOUNT_LOOKS_EU : null
}

/**
 * Convenience wrapper over {@link detectAmountFormatContradiction} that does the
 * currency-symbol/whitespace cleaning itself, for callers holding a RAW cell
 * (the import engine, which needs the reason to put on the refused row).
 */
export function detectAmountFormatMismatch(
  rawAmount: string,
  amountFormat: string,
): AmountFormatMismatch | null {
  return detectAmountFormatContradiction(
    rawAmount.trim().replace(CURRENCY_SYMBOL_PATTERN, ''),
    amountFormat,
  )
}

/**
 * Normalize a CSV amount cell to a plain US-format numeric string (currency
 * symbols/whitespace stripped, thousands separators removed, EU `,` decimal
 * mapped to `.`), WITHOUT parsing it to a float.
 *
 * This is the locale/cleaning half of {@link parseCsvAmount}, exposed
 * separately so precision-critical callers (the backend import pipeline) can
 * feed the normalized string into an arbitrary-precision Decimal instead of
 * IEEE-754 `parseFloat` — 8-18 decimal crypto amounts and >15-significant-digit
 * integers are corrupted by the float roundtrip. Keeping the cleaning logic
 * here (single source) guarantees the float and Decimal paths can never
 * disagree about WHAT string is parsed.
 *
 * @returns The normalized string, or `null` for empty input. The string is NOT
 *          validated as numeric — callers parse it (`parseFloat` leading-prefix
 *          semantics in {@link parseCsvAmount}, strict Decimal in the backend).
 */
export function normalizeCsvAmountString(value: string, amountFormat: string): string | null {
  if (!value || value.trim() === '') {
    return null
  }

  const cleaned = value.trim().replace(CURRENCY_SYMBOL_PATTERN, '')

  // Scientific notation (`2.8e-7`, `1.5E+21`) is unambiguous: the `.` is always
  // the mantissa decimal point, never a thousands separator. Return it directly
  // BEFORE the EU/US separator logic — in EU mode that logic strips every `.`
  // as a thousands separator, turning `2.8e-7` into `28e-7` (10x too large,
  // tax-critical). Exchanges/Excel emit tiny amounts this way (LEDN `2.8e-7`,
  // Bybit `0E-8`). The EU-decimal form `2,8e-7` is left for the EU branch below,
  // which correctly maps the comma to a dot.
  if (/^[+-]?\d*\.?\d+[eE][+-]?\d+$/.test(cleaned)) {
    return cleaned
  }

  // A cell that CONTRADICTS the declared format does not become a number at
  // all. Reinterpreting it is a GUESS that only "succeeds" for some rows: under
  // a US-declaring template (Binance Transaction History) a stray EU cell was
  // silently rewritten — "1.234,56" → 1.23456 (a 1000x UNDERSTATEMENT booked as
  // a valid ledger amount), "0,5" → 5 (10x the other way) — while a cell the
  // opposite convention cannot express at all failed loudly. Same file, same
  // column, two behaviours, and the corrupted figures fed the FIFO cost-basis
  // engine with no trace.
  //
  // Rule (the DATE parser's, applied to numbers — see
  // `coerceCsvDateAfterFormatMismatchToIso`, which refuses an ambiguous numeric
  // date triple after a declared-format mismatch for exactly this reason): a
  // column must behave ONE way. AMBIGUITY is untouched — "1.000"/"1,234" are
  // valid in both conventions and still parse as before, so a correctly
  // formatted file is unaffected. Callers surface the refused cell (the import
  // engine reports the detected convention on the row); nothing is guessed.
  if (detectAmountFormatContradiction(cleaned, amountFormat) !== null) {
    return null
  }

  // ⚠️⚠️ MÁS DE UN separador DECIMAL no es un número en la convención declarada.
  //
  // La rama EU cambiaba solo la PRIMERA coma (`.replace(',', '.')`), así que
  // «1,23,456» salía de aquí como `1.23,456` y el parser de prefijo —cuya
  // tolerancia es deliberada, para celdas como «334.850W»— se quedaba con
  // **1,23**. Medido de punta a punta por la UI (2026-08-12): importado con una
  // plantilla EU, ese valor llegó al libro como `1,23000000` con la insignia
  // «✓ OK» y sin un solo aviso. La rama US trunca igual con «1.23.456».
  //
  // Es el mismo defecto que este fichero ya describe para el formato
  // CONTRADICHO más arriba —«1.234,56» → 1.23456— pero por una vía que aquella
  // comprobación no cubre: no hay contradicción entre convenciones, simplemente
  // no es un número en NINGUNA.
  //
  // Devolver `null` lo convierte en lo que ya era para el resto de casos
  // ilegibles: `isUnparseableAmountCell` lo marca y la fila se muestra como
  // error, igual que una fecha inválida. No se toca la tolerancia del prefijo:
  // «0,5BTC» sigue teniendo UNA sola coma y se lee igual que antes.
  const decimalSeparator = amountFormat === 'EU' ? ',' : '.'
  if (cleaned.split(decimalSeparator).length > 2) {
    return null
  }

  if (amountFormat === 'EU') {
    return cleaned.replace(/\./g, '').replace(',', '.')
  }
  return cleaned.replace(/,/g, '')
}

/**
 * Parse a CSV amount cell into a number, honoring the EU/US decimal convention.
 *
 * ⚠️ SÓLO PARA DECIDIR, NUNCA PARA PERSISTIR. Lo que devuelve es un `double`, y
 * un importe de cripto de 9 a 18 decimales (o un entero de lamports por encima
 * de 2^53) no cabe en los 15-16 dígitos significativos de IEEE-754. Úsalo para
 * el signo, para comparar contra cero o para elegir una rama; para producir la
 * CADENA que se guarda está `parseCsvAmountToDecimal` +
 * `formatDecimalAsCsvAmount` en el backend, que trabajan sobre el texto exacto
 * de la celda.
 *
 * Este fichero TENÍA aquí mismo su formateador gemelo, `formatCsvAmount`, que
 * hacía `toFixed(8)` sobre este mismo `double`: convertía la decisión en una
 * cantidad persistida y truncaba a 8 decimales lo que la columna guarda a 18.
 * Se retiró con el issue #2197; no lo reintroduzcas.
 *
 * @param value         Raw cell value.
 * @param amountFormat  `'EU'` (`.` thousands, `,` decimal) or anything else
 *                      (treated as US: `,` thousands, `.` decimal).
 * @returns The parsed number, or `null` for empty/unparseable input.
 */
export function parseCsvAmount(value: string, amountFormat: string): number | null {
  const normalized = normalizeCsvAmountString(value, amountFormat)
  if (normalized === null) {
    return null
  }
  const parsed = parseFloat(normalized)
  return isNaN(parsed) ? null : parsed
}
